import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { BsEye, BsEyeSlashFill } from 'react-icons/bs'
import { InputGroup, Modal, Button, Spinner } from '../../../../UI'
import { createCompany, updateCompany, getCompanyDetails } from '../../../../store/actions'
import Container from './styles'

const CompanyForm = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { companyId } = useParams()
  const isEditMode = !!companyId
  
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(isEditMode)
  const [currentStep, setCurrentStep] = useState(1)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    company_name: '',
    company_email: '',
    contact_person: '',
    contact_phone: '',
    password: '',
    business_address: '',
    business_type: '',
  })

  useEffect(() => {
    if (isEditMode && companyId) {
      setFetchingData(true)
      dispatch(getCompanyDetails(companyId))
        .then((response) => {
          if (response && response.data) {
            const company = response.data
            setFormData({
              company_name: company.company_name || '',
              company_email: company.company_email || '',
              contact_person: company.contact_person || '',
              contact_phone: company.contact_phone || '',
              password: '', // Don't populate password for security
              business_address: company.business_address || '',
              business_type: company.business_type || '',
            })
          }
          setFetchingData(false)
        })
        .catch(() => {
          setFetchingData(false)
        })
    }
  }, [isEditMode, companyId, dispatch])

  const handleInput = ({ target: { value, name } }) => {
    setFormData((s) => ({
      ...s,
      [name]: value,
    }))
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const isEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const validateStep = (step) => {
    const newErrors = {}

    switch (step) {
      case 1:
        if (!formData.company_name.trim()) {
          newErrors.company_name = 'Company name is required'
        }
        if (!formData.company_email.trim()) {
          newErrors.company_email = 'Company email is required'
        } else if (!isEmail(formData.company_email)) {
          newErrors.company_email = 'Invalid email address'
        }
        break
      case 2:
        if (!formData.contact_person.trim()) {
          newErrors.contact_person = 'Contact person is required'
        }
        if (!formData.contact_phone.trim()) {
          newErrors.contact_phone = 'Contact phone is required'
        } else if (formData.contact_phone.length < 10) {
          newErrors.contact_phone = 'Phone number must be at least 10 digits'
        }
        break
      case 3:
        if (!formData.business_address.trim()) {
          newErrors.business_address = 'Business address is required'
        }
        // Password is only required for creating new company
        if (!isEditMode) {
          if (!formData.password.trim()) {
            newErrors.password = 'Password is required'
          } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters'
          }
        }
        break
      default:
        return true
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3))
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateStep(3)) return

    const { contact_phone, ...data } = formData
    setLoading(true)
    
    const payload = {
      ...data,
      contact_phone: contact_phone.includes('+234')
        ? contact_phone
        : `+234${contact_phone.slice(1)}`,
    }

    // Remove password if in edit mode and password is empty
    if (isEditMode && !payload.password) {
      delete payload.password
    }

    const action = isEditMode 
      ? updateCompany(companyId, payload)
      : createCompany(payload)
    
    dispatch(action)
      .then((response) => {
        setLoading(false)
        if (response && (response.status === 201 || response.status === 200)) {
          navigate('/dashboard/companies')
        }
      })
      .catch(() => {
        setLoading(false)
      })
  }

  if (fetchingData) {
    return (
      <Container>
        <Modal showModal={true}>
          <div className="adminForm--container">
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <Spinner size="50px" />
              <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading company data...</p>
            </div>
          </div>
        </Modal>
      </Container>
    )
  }

  return (
    <Container>
      <Modal showModal={true}>
        <div className="adminForm--container">
          <header>
            <h2>{isEditMode ? 'Edit Company' : 'Add New Company'}</h2>
            <p>Step {currentStep} of 3</p>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(currentStep / 3) * 100}%` }}
              />
            </div>
          </header>

          <form
            noValidate
            name="company-form"
            onSubmit={handleSubmit}
          >
            {/* Step 1: Company Information */}
            {currentStep === 1 && (
              <div className="form-step">
                <h3>Company Information</h3>
                <InputGroup
                  name="company_name"
                  label={'Company Name'}
                  onChange={handleInput}
                  value={formData.company_name}
                  placeholder="Enter the company name"
                />
                {errors.company_name && (
                  <p className="error-message">{errors.company_name}</p>
                )}
                
                <InputGroup
                  label={'Company Email'}
                  name="company_email"
                  value={formData.company_email}
                  onChange={handleInput}
                  type="email"
                  placeholder="company@example.com"
                />
                {errors.company_email && (
                  <p className="error-message">{errors.company_email}</p>
                )}
              </div>
            )}

            {/* Step 2: Contact Details */}
            {currentStep === 2 && (
              <div className="form-step">
                <h3>Contact Details</h3>
                <InputGroup
                  name="contact_person"
                  label={'Contact Person'}
                  onChange={handleInput}
                  value={formData.contact_person}
                  placeholder="Full name"
                />
                {errors.contact_person && (
                  <p className="error-message">{errors.contact_person}</p>
                )}
                
                <InputGroup
                  name="contact_phone"
                  label={'Contact Phone'}
                  value={formData.contact_phone}
                  type="tel"
                  onChange={handleInput}
                  placeholder="08012345678"
                />
                {errors.contact_phone && (
                  <p className="error-message">{errors.contact_phone}</p>
                )}
              </div>
            )}

            {/* Step 3: Business Details */}
            {currentStep === 3 && (
              <div className="form-step">
                <h3>Business Details</h3>
                <InputGroup
                  name="business_address"
                  label={'Business Address'}
                  onChange={handleInput}
                  value={formData.business_address}
                  placeholder="Full business address"
                />
                {errors.business_address && (
                  <p className="error-message">{errors.business_address}</p>
                )}

                <InputGroup
                  name="business_type"
                  label={'Business Type'}
                  onChange={handleInput}
                  value={formData.business_type}
                  placeholder="e.g., Logistics, E-commerce (Optional)"
                />

                <div className="password-field">
                  <InputGroup
                    label={isEditMode ? 'Password (leave empty to keep current)' : 'Password'}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInput}
                    placeholder={isEditMode ? "Leave empty to keep current password" : "Secure password for company account"}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <BsEyeSlashFill /> : <BsEye />}
                  </button>
                </div>
                {errors.password && (
                  <p className="error-message">{errors.password}</p>
                )}
              </div>
            )}

            <div className="form-actions">
              {currentStep > 1 && (
                <Button 
                  type="button" 
                  onClick={prevStep}
                  className="btn-secondary"
                >
                  Back
                </Button>
              )}
              
              {currentStep < 3 ? (
                <Button 
                  type="button" 
                  onClick={nextStep}
                  full={currentStep === 1}
                >
                  Next
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  spinnerWithTxt 
                  loading={loading}
                  full
                >
                  {isEditMode ? 'Update Company' : 'Create Company'}
                </Button>
              )}
            </div>
          </form>
        </div>
      </Modal>
    </Container>
  )
}

export default CompanyForm
